import { ArielAiNodeDataInput } from '../../generated/hooks';
import { NodeData, OutputKey } from './dataNode';

/** Apollo adds __typename to query/subscription results; GraphQL input types reject it. */
const stripTypename = <T>(value: T): T => {
  if (value === null || value === undefined) {
    return value;
  }
  if (Array.isArray(value)) {
    return value.map(stripTypename) as T;
  }
  if (typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value)
        .filter(([key]) => key !== '__typename')
        .map(([key, val]) => [key, stripTypename(val)]),
    ) as T;
  }
  return value;
};

export const nodeDataToGqlGraph = (nodes: NodeData[]): ArielAiNodeDataInput[] =>
  stripTypename(
    nodes.map((node) => ({
      questionIndex: node.questionIndex,
      questionText: node.questionText,
      isRequired: node.isRequired,
      metrics: node.metrics,
      isQuestionTrigger: node.isQuestionTrigger,
      choices: Object.entries(node.choices).map(([key, value]) => ({ key, value: String(value) })),
      statements: node.statements?.map((s) => s ?? '') ?? undefined,
      statementIds: node.statementIds ?? undefined,
      randomizeOrder: node.randomizeOrder ?? undefined,
      isRankingScale: node.isRankingScale ?? undefined,
      showEscapeOption: node.showEscapeOption ?? undefined,
      type: node.type,
      range: node.range ?? undefined,
      answersToHighlight: node.answersToHighlight
        ? Object.entries(node.answersToHighlight).map(([key, value]) => ({ key, value: String(value) }))
        : undefined,
      input: node.input,
      outputs: Object.entries(node.outputs).map(([key, targetQuestionIndex]) => ({
        key,
        targetQuestionIndex: targetQuestionIndex ?? undefined,
      })),
      position: node.position ?? undefined,
      reportOrder: node.reportOrder ?? undefined,
      comments: undefined,
      commentGroupId: node.commentGroupId ?? undefined,
    })),
  );

type GqlGraphNode = Pick<
  ArielAiNodeDataInput,
  | 'questionIndex'
  | 'questionText'
  | 'isRequired'
  | 'metrics'
  | 'isQuestionTrigger'
  | 'choices'
  | 'statements'
  | 'statementIds'
  | 'randomizeOrder'
  | 'isRankingScale'
  | 'showEscapeOption'
  | 'type'
  | 'range'
  | 'answersToHighlight'
  | 'input'
  | 'outputs'
  | 'position'
  | 'reportOrder'
  | 'commentGroupId'
>;

export const gqlGraphToNodeData = (graph: GqlGraphNode[]): NodeData[] =>
  stripTypename(graph).map((node) => ({
    questionIndex: node.questionIndex,
    questionText: node.questionText,
    isRequired: node.isRequired,
    metrics: node.metrics,
    isQuestionTrigger: node.isQuestionTrigger,
    choices: Object.fromEntries(node.choices.map((c) => [c.key as OutputKey, c.value])),
    statements: node.statements ? node.statements.map((s) => s ?? undefined) : undefined,
    statementIds: node.statementIds ? node.statementIds.map((id) => id ?? '') : undefined,
    randomizeOrder: node.randomizeOrder ?? undefined,
    isRankingScale: node.isRankingScale ?? undefined,
    showEscapeOption: node.showEscapeOption ?? undefined,
    type: node.type as NodeData['type'],
    range: node.range ?? undefined,
    answersToHighlight: node.answersToHighlight
      ? Object.fromEntries(node.answersToHighlight.map((e) => [e.key as OutputKey, Number(e.value)]))
      : undefined,
    input: node.input,
    outputs: Object.fromEntries(node.outputs.map((o) => [o.key as OutputKey, o.targetQuestionIndex ?? null])),
    position: (node.position ?? [null, null]) as NodeData['position'],
    reportOrder: node.reportOrder ?? null,
    commentGroupId: node.commentGroupId ?? undefined,
    topicId: undefined,
  }));
